import { i18n } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChartLine, CircleCheck, UserRound } from "lucide-react";
import { autorun } from "mobx";
import { observer } from "mobx-react-lite";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import type { AppModel } from "../../app/app-model";
import type { MetricValue, PatientMetrics } from "./daily-monitoring-metrics";

import { type DefaultPatientEventResponse, PermissionBit } from "../../api";
import { ErrorPage } from "../../app/error-page";
import { PageHeaderActions } from "../../app/layout/layout-context";
import { AgeSexBadge, ageSexOf } from "../../components/age-sex-badge";
import {
  AssignedOnlyToggle,
  useAssignedOnly,
} from "../../components/assigned-only-toggle";
import { CellNestedAlerts } from "../../components/monitoring-rooms/cell-nested-alerts";
import { Button } from "../../components/ui-kit/base/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui-kit/base/sheet";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui-kit/base/tabs";
import { Spinner } from "../../components/ui-kit/spinner";
import { CellTooltipList } from "../../components/ui-kit/tables/cells/cell-tooltip-list";
import {
  type Column,
  DataTable,
  ROW_TINT_SUCCESS,
} from "../../components/ui-kit/tables/data-table";
import {
  hasMedicalAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../../constants/permissions";
import { DataModel } from "../../models/data-model";
import { consentOk } from "../../utils/data-utils";
import { DeviceConfigIcons } from "../../utils/device-config-filter";
import { getAge } from "../../utils/utils";
import {
  DailyMonitoringModel,
  type MonitoringRow,
} from "./daily-monitoring-model";
import { MonitoringEcgCell } from "./monitoring-ecg-cell";
import { MonitoringLastMeasuredCell } from "./monitoring-last-measured-cell";
import { MetricCell } from "./monitoring-metric-cell";
import { ResolveDialog } from "./resolve-dialog";
import { SignalIndicator } from "./signal-indicator";
import { TrendsCharts } from "./trends-charts";

export const DailyMonitoringRoute = observer(function DailyMonitoringRoute({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const [assignedOnly, setAssignedOnly] = useAssignedOnly("daily-monitoring");
  const [tab, setTab] = useState<"completed" | "not-completed">("completed");

  const permissions = app.seerlinqApi.permissions;
  const [model, setModel] = useState<DailyMonitoringModel>();
  useEffect(() => {
    const next = new DailyMonitoringModel(assignedOnly, permissions);
    setModel(next);
    void next.load();
  }, [assignedOnly, permissions]);

  const [completedTable] = useState(() => makeTableModel(app));
  const [notCompletedTable] = useState(() => makeTableModel(app));

  useEffect(() => {
    if (!model) return;
    return autorun(() => {
      completedTable.updateData(model.completedRows);
      notCompletedTable.updateData(model.notCompletedRows);
    });
  }, [model, completedTable, notCompletedTable]);

  if (!model) return <Spinner />;

  if (model.error) {
    return (
      <ErrorPage
        title={t`Error loading daily monitoring`}
        message={model.error}
      />
    );
  }

  const summary = model.summary;
  const reviewedCount = summary.reviewed;
  const unseenAlertsCount = summary.withUnseenAlerts;
  const can = dmPermissions(permissions);
  const downloadBase = app.seerlinqApi.versionedRoute();
  const completedColumns = [
    ...makeColumns(can),
    ...makeMetricColumns(model, can),
    can.medical && makeEcgColumn(model, downloadBase),
    ...makeActionColumns(model, can, {
      resolve: true,
      group: i18n._(GROUPS.actions),
    }),
  ];
  const notCompletedColumns = makeNotCompletedColumns(model, can);

  const isCompleted = tab === "completed";
  const completedCount = summary.completed;
  const notCompletedCount = model.notCompletedRows.length;
  // Tabs live in the table's action row (like the Patients table), so a single
  // DataTable renders the active tab's model/columns rather than one table each.
  const tabsList = (
    <TabsList className="w-auto border-b-0">
      <TabsTrigger value="completed" className="flex-none">
        <Trans>Completed today ({completedCount})</Trans>
      </TabsTrigger>
      <TabsTrigger value="not-completed" className="flex-none">
        <Trans>Not completed ({notCompletedCount})</Trans>
      </TabsTrigger>
    </TabsList>
  );

  return (
    <>
      <PageHeaderActions>
        {hasMonitoringAccess(app.seerlinqApi.permissions) ? (
          <AssignedOnlyToggle value={assignedOnly} onChange={setAssignedOnly} />
        ) : undefined}
      </PageHeaderActions>

      <div className="mb-4 flex items-center gap-6">
        <div>
          <span
            data-testid="dm-summary-completed"
            className="text-2xl font-semibold"
          >
            {summary.completed}
          </span>
          <span className="text-muted-foreground"> / </span>
          <span data-testid="dm-summary-total">{summary.total}</span>
          <span className="text-muted-foreground">
            {" "}
            <Trans>completed</Trans>
          </span>
        </div>
        <div className="h-2 w-40 overflow-hidden rounded bg-muted">
          <div
            className="h-full bg-brand-primary"
            style={{ width: `${summary.completionPct}%` }}
          />
        </div>
        {/* Both read a permanent 0 for a user who can't fetch what they
            count, so they follow their columns' permissions. */}
        {can.events && (
          <div data-testid="dm-summary-reviewed">
            <Trans>Reviewed: {reviewedCount}</Trans>
          </div>
        )}
        {can.waitingRoom && (
          <div data-testid="dm-summary-alerts">
            <Trans>With unseen alerts: {unseenAlertsCount}</Trans>
          </div>
        )}
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as "completed" | "not-completed")}
      >
        {isCompleted ? (
          <DataTable
            key="completed"
            model={completedTable}
            columns={completedColumns}
            itemKeyField="patient_id"
            isLoading={model.loading}
            filterNoun={(count) =>
              t`${plural(count, { one: "patient", other: "patients" })}`
            }
            filterActions={tabsList}
            rowClassName={(item) =>
              item.resolved ? ROW_TINT_SUCCESS : undefined
            }
          />
        ) : (
          <DataTable
            key="not-completed"
            model={notCompletedTable}
            columns={notCompletedColumns}
            itemKeyField="patient_id"
            isLoading={model.loading}
            filterNoun={(count) =>
              t`${plural(count, { one: "patient", other: "patients" })}`
            }
            filterActions={tabsList}
          />
        )}
      </Tabs>

      <TrendsSheet model={model} />
    </>
  );
});

/**
 * Stable identity for "no events yet": the charts memoize their event list on
 * this prop, so a fresh literal each render would rebuild it every time.
 */
const NO_EVENTS: DefaultPatientEventResponse[] = [];

/**
 * The open row's charts ("Trends"), in a side sheet rather than inline in the
 * table. Inline, the panel inherited the table's full scroll width (so the
 * charts' right-hand side sat outside the horizontally-scrolling window) and
 * its height ran past the fold, which took two nested scrolls to read. The
 * sheet is portalled out of the table, so it gets a viewport of its own.
 */
const TrendsSheet = observer(function TrendsSheet({
  model,
}: {
  model: DailyMonitoringModel;
}) {
  const patientId = model.reportPatientId;
  const row = model.rows.find((r) => r.patient_id === patientId);
  // Only consent is checked here: the action that opens this sheet exists only
  // for a user holding RUN_HEART_CORE_QUICKLOOK (see `dmPermissions`), so the
  // permission can't be missing by the time it is open.
  const available = row != null && consentOk(row.patient);

  // The events timeline above the charts, as on the profile's HeartCore tab.
  // Fetched here rather than per row: only the open sheet shows it — and only
  // when the charts it sits above will actually render, so a patient without
  // consent isn't fetched for a panel that shows a message instead.
  useEffect(() => {
    if (patientId != null && available) void model.ensureEvents(patientId);
  }, [model, patientId, available]);
  const eventsEntry =
    patientId != null ? model.events.get(patientId) : undefined;

  return (
    <Sheet
      open={patientId != null}
      onOpenChange={(open) => {
        if (!open) model.closeReport();
      }}
    >
      <SheetContent
        // 80% of the viewport — the charts are the content here, and the
        // default sheet is sized for forms. Capped so the plots stop growing
        // once they are comfortably wide: each chart reserves a fixed 316px
        // summary gutter (SUMMARY_GUTTER) and only the plot stretches, so on a
        // very wide display an uncapped panel spreads a handful of weekly
        // points across the room. 1600px leaves ~1230px of plot; below roughly
        // 1030px of viewport the panels stack instead (MIN_CHART_WIDTH).
        className="w-[80vw] max-w-[1600px] gap-0 p-0"
      >
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle className="flex items-center gap-2 pr-8">
            <span>
              <Trans>Trends</Trans>
              {row && (
                <>
                  {" — "}
                  <span className="text-muted-foreground">
                    #{row.patient_id}
                  </span>{" "}
                  {row.patient.full_name}
                </>
              )}
            </span>
            {/* No `dateOfBirth`: that turns the badge into a focusable
                  reveal-and-copy button, and the sheet autofocuses its first
                  control on open — which would put a date of birth on screen
                  just for opening the charts. */}
            {row && <AgeSexBadge {...ageSexOf(row.patient)} />}
          </SheetTitle>
        </SheetHeader>
        {patientId != null &&
          (available ? (
            // The scroll container the charts' events and zoom bars stick
            // against. It carries no *vertical* padding: Chrome insets a
            // sticky element's constraint rect by the scroll container's own
            // padding, so a `pt`/`pb` here would pin both bars that far inside
            // the panel and leave a strip of chart sliding visibly past them.
            // The top gap the zoom presets need goes on an inner wrapper,
            // where it scrolls away like any other content.
            <div
              data-testid={`dm-trends-panel-${patientId}`}
              className="flex-1 overflow-y-auto px-6"
            >
              <div className="pt-4">
                <TrendsCharts
                  patientId={patientId}
                  events={
                    eventsEntry?.status === "loaded"
                      ? eventsEntry.events
                      : NO_EVENTS
                  }
                />
              </div>
            </div>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              <Trans>Charts are not available for this patient.</Trans>
            </p>
          ))}
      </SheetContent>
    </Sheet>
  );
});

/**
 * What this user's permissions let the worklist show. The route is reachable on
 * GROUP_MONITORING_ROUTERS plus the feature flag, but every column behind it
 * reads an endpoint with a bit of its own — so without this a user would get
 * columns that can only ever render a dash and a Resolve button that 403s.
 * Each flag names the bit the API itself enforces (see the model's fetches).
 */
function dmPermissions(permissions: PermissionBit[]) {
  return {
    /** Metric + ECG columns: computed / derived / vitals / basics / symptoms
     *  / medical-reports all require the medical-router group. */
    medical: hasMedicalAccess(permissions),
    /** LVFP is additionally gated the way it is everywhere else in the app
     *  (nav, profile chart tabs, QuickLook), not just by its endpoint. */
    lvfp: hasPermission(permissions, PermissionBit.ACCESS_LVFP),
    /** Signal reads the patient's PPG list. */
    ppgMeta: hasPermission(permissions, PermissionBit.PPG_META),
    /** Alerts come from the two waiting rooms. */
    waitingRoom: hasPermission(permissions, PermissionBit.VIEW_WAITING_ROOM),
    /** Resolve reads and writes patient events. */
    events: hasPermission(permissions, PermissionBit.VIEW_EVENTS),
    /** Trends submits a QuickLook; Last measured reads monitoring-info. */
    trends: hasPermission(permissions, PermissionBit.RUN_HEART_CORE_QUICKLOOK),
  };
}

type DmPermissions = ReturnType<typeof dmPermissions>;

// Columns are grouped by string equality (adjacent columns sharing a `group`
// span one header cell), so each group name must resolve to one exact string —
// the same rule the patients table's filter groups follow.
const GROUPS = {
  patient: msg`Patient`,
  ppgDerived: msg`PPG derived`,
  vitals: msg`Vitals`,
  symptoms: msg`Symptoms`,
  ecg: msg`ECG`,
  actions: msg`Actions`,
};

function makeTableModel(app: AppModel): DataModel<MonitoringRow> {
  const m = new DataModel<MonitoringRow>(
    [],
    app.seerlinqApi.permissions,
    app.seerlinqApi.requireUserUUID(),
  );
  m.idAttr = "patient_id";
  return m;
}

// Identity columns for the Completed table. The group is applied here rather
// than on the shared column consts, which the Not-completed (ungrouped) table
// reuses.
function makeColumns(can: DmPermissions): Column<MonitoringRow>[] {
  const identity: Array<Column<MonitoringRow> | false> = [
    { ...nameColumn, sticky: "left" },
    ageGenderColumn,
    can.waitingRoom && alertsColumn,
  ];
  return identity
    .filter((col): col is Column<MonitoringRow> => col !== false)
    .map((col) => ({ ...col, group: i18n._(GROUPS.patient) }));
}

// Name and patient ID in one column. The ID used to be a column of its own,
// linking to the profile; navigation now lives in the row's action buttons, so
// here the ID is only the identifier a physician recognises the patient by —
// hence subordinate to the name rather than beside it.
const nameColumn: Column<MonitoringRow> = {
  header: <Trans>Name</Trans>,
  width: 190,
  align: "left",
  cell: ({ item }) => (
    <span data-testid={`dm-name-${item.patient_id}`}>
      <span className="font-medium">{item.patient.full_name}</span>{" "}
      <span className="text-xs text-muted-foreground">#{item.patient_id}</span>
    </span>
  ),
};

// Age / Gender in its own column, mirroring patients-route.tsx.
const ageGenderColumn: Column<MonitoringRow> = {
  header: <Trans>Age / Gender</Trans>,
  width: 90,
  cell: ({ item }) => {
    const { patient } = item;
    const age = patient.date_of_birth ? getAge(patient.date_of_birth) : "–";
    const sex = patient.sex ?? "–";
    return (
      <span>
        {age} {sex}
      </span>
    );
  },
};

// Reuses the waiting-rooms alerts cell (count + popover DataTable), opting into
// the Daily-Monitoring-only extras: a filterable table and the per-patient
// mark-all-seen action. Combining medical + technical into one array keeps the
// count consistent with the rows shown in the popover.
const alertsColumn: Column<MonitoringRow> = {
  header: <Trans>Alerts</Trans>,
  width: 120,
  cell: ({ item }) => (
    <div
      data-testid={`dm-alerts-${item.patient_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <CellNestedAlerts
        alerts={item.alerts}
        filterable
        markAllSeenFor={item.patient_id}
      />
    </div>
  ),
};

/**
 * The row's actions, always visible at the end of the row — one column each, so
 * the column header names the action and the icon needs no tooltip to be read.
 * Trends and Profile were previously the row-expander caret and the clickable
 * patient ID, both discoverable only by trying them, which is what this fixes.
 *
 * Resolve is Completed-only: a patient who hasn't measured today has no review
 * to sign off. `group` is passed by the Completed table (whose columns are all
 * grouped) so the three span one "Actions" header, and left off by the
 * Not-completed one, which has no group row.
 */
function makeActionColumns(
  model: DailyMonitoringModel,
  can: DmPermissions,
  opts: { resolve: boolean; group?: string },
): Column<MonitoringRow>[] {
  const columns: Column<MonitoringRow>[] = [];
  if (can.trends) {
    columns.push({
      header: <Trans>Trends</Trans>,
      width: ACTION_COLUMN_WIDTH,
      sticky: "right",
      cell: ({ item }) => (
        <ActionCell>
          <ActionButton
            label={i18n._(msg`Trends`)}
            testId={`dm-trends-${item.patient_id}`}
            onClick={() => model.openReport(item.patient_id)}
          >
            <ChartLine className="size-4" />
          </ActionButton>
        </ActionCell>
      ),
    });
  }
  columns.push({
    header: <Trans>Profile</Trans>,
    width: ACTION_COLUMN_WIDTH,
    sticky: "right",
    cell: ({ item }) => (
      <ActionCell>
        <ActionButton
          label={i18n._(msg`Patient profile`)}
          testId={`dm-profile-${item.patient_id}`}
          to={`/patient/${item.patient_id}`}
        >
          <UserRound className="size-4" />
        </ActionButton>
      </ActionCell>
    ),
  });
  if (opts.resolve && can.events) {
    columns.push({
      header: <Trans>Resolve</Trans>,
      width: ACTION_COLUMN_WIDTH,
      sticky: "right",
      cell: ({ item }) => (
        <ActionCell>
          {item.resolved ? (
            // Resolved: a plain, non-clickable green check. Not a disabled
            // button (which dims the icon) — kept within the action button's
            // footprint.
            <span
              data-testid={`dm-resolved-${item.patient_id}`}
              aria-label={i18n._(msg`Resolved`)}
              role="img"
              className="inline-flex size-8 items-center justify-center text-success-foreground"
            >
              <CircleCheck className="size-5" />
            </span>
          ) : (
            <ResolveDialog
              row={item}
              onResolved={() => model.markResolvedLocally(item.patient_id)}
            />
          )}
        </ActionCell>
      ),
    });
  }
  return opts.group
    ? columns.map((col) => ({ ...col, group: opts.group }))
    : columns;
}

/**
 * The floor for these columns: the widest action header ("Resolve") measures
 * ~41px, and the table's cells pad 8px a side. Declaring less doesn't shrink
 * them — the header outgrows its column and the browser widens it anyway,
 * which also leaves the three unequal.
 */
const ACTION_COLUMN_WIDTH = 58;

/** Centres an action and keeps its click off the row. */
function ActionCell({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * One icon action — a button, or a link when `to` is given. Icon-only: the
 * column header carries the name on screen, so `label` is only the accessible
 * name the icon itself can't provide.
 *
 * `icon-sm` is a fixed 32px square, which is what keeps the actions (and the
 * resolved check, also size-8) the same width. The padded sizes don't: their
 * icon-only padding is keyed off `has-[>svg]`, which matches the link (whose
 * icon is a direct child) but not the buttons (whose children are wrapped),
 * leaving the link 2px narrower.
 */
function ActionButton({
  label,
  testId,
  to,
  onClick,
  children,
}: {
  label: string;
  testId: string;
  to?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  return to ? (
    // As a link this would take the global anchor colour and read as the odd
    // one out. The buttons beside it get their colour from the base `button`
    // rule, so match that token rather than the (lighter) plain foreground.
    <Button
      variant="outline"
      size="icon-sm"
      asChild
      className="text-secondary-foreground"
    >
      <Link to={to} aria-label={label} data-testid={testId}>
        {children}
      </Link>
    </Button>
  ) : (
    <Button
      variant="outline"
      size="icon-sm"
      aria-label={label}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

// Metric columns for the Completed table — each shows the patient's latest
// value for one measurement, fetched lazily per row (see MetricCell). They live
// on the Completed table only: they represent today's readings, which a
// not-completed patient by definition doesn't have. Units sit in the headers to
// keep cells compact; column order follows the requested grouping.
function num(m: MetricValue, digits: number): string {
  return m ? m.value.toFixed(digits) : "–";
}

function makeMetricColumns(
  model: DailyMonitoringModel,
  can: DmPermissions,
): Column<MonitoringRow>[] {
  const col = (
    group: string,
    header: ReactNode,
    testId: string,
    render: (m: PatientMetrics) => ReactNode,
  ): Column<MonitoringRow> => ({
    group,
    header,
    width: 90,
    cell: ({ item }) => (
      <MetricCell
        model={model}
        patientId={item.patient_id}
        testId={`dm-metric-${testId}-${item.patient_id}`}
        render={render}
      />
    ),
  });
  const ppgDerived = i18n._(GROUPS.ppgDerived);
  const vitals = i18n._(GROUPS.vitals);
  const symptoms = i18n._(GROUPS.symptoms);
  // A column whose endpoint this user can't read is dropped, not left to render
  // a permanent dash. Column order is preserved, so the group headers still
  // span whatever survives.
  return [
    can.medical &&
      can.lvfp &&
      col(ppgDerived, <Trans>LVFP (mmHg)</Trans>, "lvfp", (m) =>
        num(m.lvfp, 1),
      ),
    can.medical &&
      col(ppgDerived, <Trans>HR (bpm)</Trans>, "hr", (m) => num(m.hr, 0)),
    can.medical &&
      col(ppgDerived, <Trans>SpO₂ (%)</Trans>, "spo2", (m) => num(m.spo2, 0)),
    can.ppgMeta &&
      col(ppgDerived, <Trans>Signal</Trans>, "signal", (m) => (
        <SignalIndicator sqi={m.signal} flag={m.signalFlag} />
      )),
    can.medical &&
      col(vitals, <Trans>BP (mmHg)</Trans>, "bp", (m) =>
        m.systolic || m.diastolic
          ? `${num(m.systolic, 0)}/${num(m.diastolic, 0)}`
          : "–",
      ),
    can.medical &&
      col(vitals, <Trans>Weight (kg)</Trans>, "weight", (m) =>
        num(m.weight, 1),
      ),
    can.medical &&
      col(symptoms, <Trans>Breathless</Trans>, "breathless", (m) =>
        num(m.breathless, 0),
      ),
    can.medical &&
      col(symptoms, <Trans>Fatigue</Trans>, "fatigue", (m) =>
        num(m.fatigue, 0),
      ),
    can.medical &&
      col(symptoms, <Trans>Health Change</Trans>, "health-change", (m) =>
        num(m.relScore, 0),
      ),
  ].filter((c): c is Column<MonitoringRow> => c !== false);
}

// ECG reports column — opens uploaded ECG report file(s) for the patient.
// Narrow: the cell is icons, one per report, with no label to fit.
function makeEcgColumn(
  model: DailyMonitoringModel,
  downloadBase: string,
): Column<MonitoringRow> {
  return {
    group: i18n._(GROUPS.ecg),
    header: <Trans>ECG</Trans>,
    width: 72,
    cell: ({ item }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <MonitoringEcgCell
          model={model}
          patientId={item.patient_id}
          downloadBase={downloadBase}
        />
      </div>
    ),
  };
}

// The Not-completed table gets its own column set rather than reusing
// `makeColumns` — Physician/Last measured/Devices are meaningless for the
// Completed table. It carries the same identity and action columns, minus
// Resolve (see makeActionsColumn).
function makeNotCompletedColumns(
  model: DailyMonitoringModel,
  can: DmPermissions,
): Column<MonitoringRow>[] {
  const columns: Array<Column<MonitoringRow> | false> = [
    nameColumn,
    ageGenderColumn,
    can.waitingRoom && alertsColumn,
    {
      header: <Trans>Physician</Trans>,
      width: 160,
      cell: ({ item }) => (
        <CellTooltipList
          items={(item.patient.physicians ?? []).map((p) => p.full_name)}
          empty="–"
        />
      ),
    },
    can.trends && {
      header: <Trans>Last measured</Trans>,
      width: 140,
      // Fetched lazily per row, like the Completed table's metric cells — only
      // the visible page's patients ever call `monitoring-info`.
      cell: ({ item }) => (
        <MonitoringLastMeasuredCell model={model} patientId={item.patient_id} />
      ),
    },
    {
      // Same icon row as the patients table's Device config column — what the
      // patient is expected to measure reads faster than a bare count.
      header: <Trans>Devices</Trans>,
      width: 120,
      cell: ({ item }) => (
        <DeviceConfigIcons config={item.patient.self_measurement_config} />
      ),
    },
    ...makeActionColumns(model, can, { resolve: false }),
  ];
  return columns.filter((col): col is Column<MonitoringRow> => col !== false);
}
